<template>
    <div class="table-card card">
      <div class="table-header">
        <h2 class="table-title">System Administrators</h2>
        <button class="btn btn-primary" @click="$emit('create')">
          + Add Admin
        </button>
      </div>

      <div v-if="isLoading" class="flex justify-center mt-5">
        <loading size="15px" />
      </div>

      <alt-table
        v-else
        :columns="columns"
        pagination
        :paginationOptions="paginationOptions"
        @onPagination="$emit('onPagination', $event)"
      >
        <template #t-row>
          <tr v-for="(admin, index) in admins" :key="admin.id">
            <td>{{ index + 1 }}</td>
            <td class="id-cell">{{ admin.id }}</td>
            <td>{{ admin.username }}</td>
            <td>{{ admin.email }}</td>
            <td>
              <AdminStatusBadge :isActive="admin.isActive" />
            </td>
            <td class="actions">
              <button class="btn-action" @click="$emit('edit', admin)" title="Edit">
                ✏️
              </button>
              <button
                class="btn-status"
                @click="confirmStatusChange(admin)"
                :title="admin.isActive ? 'Deactivate' : 'Activate'"
              >
                {{ admin.isActive ? '❌' : '✅' }}
              </button>
            </td>
          </tr>
        </template>
      </alt-table>
    </div>

    <ModalConfirm
      v-model:visible="showStatusModal"
      :title="statusModalTitle"
      @confirm="executeStatusChange"
    />
  </template>

  <script setup>
  import { ref, computed } from 'vue';
  import AltTable from '@/components/tables/altTable.vue';
  import Loading from '@/components/loading.vue';
  import ModalConfirm from '@/components/ModalConfirm.vue';
  import AdminStatusBadge from '../atoms/AdminStatusBadge.vue';

  const props = defineProps({
    admins: Array,
    paginationOptions: Object,
    isLoading: Boolean,
    columns: {
      type: Array,
      default: () => [
        { header: 'N°' },
        { header: 'ID' },
        { header: 'Name' },
        { header: 'Email' },
        { header: 'Status' },
        { header: 'Actions' }
      ]
    }
  });

  const emit = defineEmits(['onPagination', 'create', 'edit', 'changeStatus']);

  const showStatusModal = ref(false);
  const selectedAdmin = ref(null);

  const statusModalTitle = computed(() => {
    if (!selectedAdmin.value) return '';
    const action = selectedAdmin.value.isActive ? 'deactivate' : 'activate';
    return `Are you sure you want to ${action} ${selectedAdmin.value.username}?`;
  });

  const confirmStatusChange = (admin) => {
    selectedAdmin.value = admin;
    showStatusModal.value = true;
  };

  const executeStatusChange = () => {
    emit('changeStatus', {
      ...selectedAdmin.value,
      status: selectedAdmin.value.isActive ? 'inactive' : 'active'
    });
    showStatusModal.value = false;
  };
  </script>

  <style scoped lang="scss">
  .table-card {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .table-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem 1.5rem 1rem 1.5rem;
      border-bottom: 1px solid #e0e0e0;

      .table-title {
        margin: 0;
        color: var(--primary-color, #007bff);
        font-size: 1.5rem;
        font-weight: 600;
      }

      .btn {
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s ease;

        &.btn-primary {
          background-color: var(--primary-color, #007bff);
          color: white;

          &:hover {
            background-color: var(--primary-color-dark, #0056b3);
          }
        }
      }
    }

    .actions {
      display: flex;
      justify-content: center;
      gap: 0.5rem;

      .btn-action,
      .btn-status {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 1.1rem;
        padding: 0.25rem;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
          background-color: #f8f9fa;
          transform: scale(1.1);
        }
      }

      .btn-status {
        font-size: 1rem;
      }
    }


    .flex {
      display: flex;

      &.justify-center {
        justify-content: center;
      }
    }

    .mt-5 {
      margin-top: 3rem;
    }

    .id-cell {
      font-family: monospace;
      font-size: 0.85rem;
      color: #6c757d;
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  </style>